import { BaseComponent } from "utils/base-component"
import { trackSignUp } from "components/signup/services/analytics"

import "./signup-page.scss"
import "./signup-full-page.scss"
import "./static/snow-forecast_outside--header_logo.webp"

// email validation (basic regex string@string.string)
function validateEmail(email: String) {
  return String(email)
    .toLowerCase()
    .match(/^\S+@\S+\.\S+$/)
}

// password validation (at least 8 characters)
function validatePassword(password: String) {
  return String(password).length >= 8
}

// The page is left right after a successful registration, so hold the redirect
// until GTM reports the sign_up event as dispatched (eventCallback), with a
// timeout in case GTM is blocked or absent.
function redirectAfterSignUpTracking(redirect: () => void) {
  let redirected = false
  const go = () => {
    if (redirected) return
    redirected = true
    redirect()
  }
  trackSignUp("email", { eventCallback: go, eventTimeout: 500 })
  window.setTimeout(go, 700)
}

async function submitForm(form: HTMLFormElement, email: String, password: String) {
  const errorContainer = document.querySelector(".js-signup-error") as HTMLElement

  try {
    if (errorContainer) errorContainer.innerHTML = ""

    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)
      ?.content

    const response = await fetch(form.action, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({
        user: {
          email,
          password,
        },
      }),
    })

    // Get the response body regardless of status
    const responseText = await response.text()
    // eslint-disable-next-line no-console
    // console.log('Response text:', responseText)

    // Try to parse as JSON if possible
    let data
    try {
      data = JSON.parse(responseText)
    } catch (e) {
      // eslint-disable-next-line no-console
      // console.error('Failed to parse response as JSON:', e)
      throw new Error("Invalid response format")
    }

    if (!response.ok) {
      if (data) {
        // console.log('Response data:', data)
        // Handle validation errors
        if (data.errors && data.errors[0].message.includes("used")) {
          // console.log('Email taken error:', data.errors[0])
          // Show email taken error
          if (errorContainer) {
            errorContainer.innerHTML = "<p>This email address is already in use.</p>"
            errorContainer.style.display = "block"
          }
          return
        }

        throw new Error("Validation failed")
      }
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    // Successful registration
    if (data.success) {
      const redirectUrl = new URL(data.redirect_url || "/my", window.location.origin) // eslint-disable-line compat/compat
      if (redirectUrl.origin !== window.location.origin) throw new Error("Invalid redirect URL")

      redirectAfterSignUpTracking(() => window.location.replace(redirectUrl.href))
    } else {
      window.location.replace("/sign_up")
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Registration failed:", error)
    // Show general error message
    if (errorContainer) {
      errorContainer.innerHTML = "<p>Something went wrong. Please try again later.</p>"
      errorContainer.style.display = "block"
    }
  }
}

export class Signup extends BaseComponent {
  static cssClass = "signup-page"

  init() {
    this.elements("signup-container").forEach((item) => {
      const backBtn = item.element.querySelector(".js-back-btn")
      const emailBtn = item.element.querySelector(".js-email-btn")
      const buttonContainer = item.element.querySelector(".js-button-container")
      const emailFormContainer = item.element.querySelector(".js-email-form")
      const emailForm = emailFormContainer?.querySelector("form") as HTMLFormElement

      const emailInput = emailFormContainer?.querySelector(
        'input[name="user[email]"]',
      ) as HTMLInputElement
      const passwordInput = emailForm?.querySelector(
        'input[name="user[password]"]',
      ) as HTMLInputElement

      const emailErr = item.element.querySelector(".js-email-error")
      const passwordErr = item.element.querySelector(".js-password-error")
      const passwordToggle = item.element.querySelector(".js-password-toggle") as HTMLElement
      const eyeHideIcon = passwordToggle?.querySelector(".js-eye-hide")
      const eyeShowIcon = passwordToggle?.querySelector(".js-eye-show")
      const toggleLabel = passwordToggle?.querySelector(".js-password-toggle-label")

      // Check if all required elements exist
      if (
        !backBtn ||
        !emailBtn ||
        !buttonContainer ||
        !emailFormContainer ||
        !emailForm ||
        !emailInput ||
        !passwordInput ||
        !emailErr ||
        !passwordErr ||
        !passwordToggle ||
        !eyeHideIcon ||
        !eyeShowIcon ||
        !toggleLabel
      ) {
        // eslint-disable-next-line no-console
        console.error("Signup component: Required elements not found")
        // console.error('emailBtn:', emailBtn)
        return
      }

      const setPasswordVisible = (visible: boolean) => {
        passwordInput.type = visible ? "text" : "password"
        eyeHideIcon.classList.toggle("signup-page__eye-icon--hidden", visible)
        eyeShowIcon.classList.toggle("signup-page__eye-icon--hidden", !visible)
        const label = visible ? passwordToggle.dataset.labelHide : passwordToggle.dataset.labelShow
        if (label) {
          passwordToggle.setAttribute("aria-label", label)
          toggleLabel.textContent = label
        }
      }

      // animations for email form when user clicks email button
      emailBtn.addEventListener("click", () => {
        setTimeout(() => {
          emailFormContainer.classList.add("show-form")
          buttonContainer.classList.add("hide-buttons")
          emailFormContainer.classList.remove("hide-form")
          buttonContainer.classList.remove("show-buttons")
          buttonContainer.classList.add("hidden")
          emailFormContainer.classList.remove("hidden")
          backBtn.classList.remove("hidden")
        }, 25)
      })

      // animations and state reset when user clicks back button
      backBtn.addEventListener("click", () => {
        setTimeout(() => {
          emailFormContainer.classList.add("hide-form")
          buttonContainer.classList.add("show-buttons")
          emailFormContainer.classList.remove("show-form")
          buttonContainer.classList.remove("hide-buttons")
          buttonContainer.classList.remove("hidden")
          emailFormContainer.classList.add("hidden")
          backBtn.classList.add("hidden")
          emailInput.value = ""
          emailInput.style.border = "1px solid #BFBFBF"
          emailErr.classList.remove("show")
          passwordInput.value = ""
          passwordInput.style.border = "1px solid #BFBFBF"
          passwordErr.classList.remove("show")
          setPasswordVisible(false)
        }, 25)
      })

      // check email validation on focus change and provide ui feedback
      emailInput.addEventListener("blur", () => {
        if (validateEmail(emailInput.value)) {
          emailInput.style.border = "2px solid green"
          emailErr.classList.remove("show")
        } else if (emailInput.value === "") {
          emailInput.style.border = "1px solid #BFBFBF"
          emailErr.classList.remove("show")
        } else {
          emailInput.style.border = "2px solid #D41121"
          emailErr.classList.add("show")
        }
      })

      // check email validation on keypress and provide ui feedback
      emailInput.addEventListener("input", () => {
        if (validateEmail(emailInput.value)) {
          emailInput.style.border = "2px solid green"
          emailErr.classList.remove("show")
        } else if (emailInput.value === "") {
          emailInput.style.border = "1px solid #BFBFBF"
          emailErr.classList.remove("show")
        }
      })

      // check password validation and provide ui feedback
      passwordInput.addEventListener("blur", () => {
        if (validatePassword(passwordInput.value)) {
          passwordInput.style.border = "2px solid green"
          passwordErr.classList.remove("show")
        } else if (passwordInput.value === "") {
          passwordInput.style.border = "1px solid #BFBFBF"
          passwordErr.classList.remove("show")
        } else {
          passwordInput.style.border = "2px solid #D41121"
          passwordErr.classList.add("show")
        }
      })

      // toggle input type and update icon plus label
      passwordToggle.addEventListener("click", () => {
        setPasswordVisible(passwordInput.type === "password")
      })

      emailForm.addEventListener("submit", async (e) => {
        e.preventDefault()

        const isEmailValid = validateEmail(emailInput.value)
        const isPasswordValid = validatePassword(passwordInput.value)

        if (!isEmailValid) {
          emailInput.style.border = "2px solid #D41121"
          emailErr.classList.add("show")
          emailInput.focus()
          return
        }

        if (!isPasswordValid) {
          passwordInput.style.border = "2px solid #D41121"
          passwordErr.classList.add("show")
          passwordInput.focus()
          return
        }

        await submitForm(emailForm, emailInput.value, passwordInput.value)
      })
    })
  }
}
